import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import {
  buildCreateFormatTemplate,
  buildIssueFormatTemplateDiagramUpload,
  buildUpdateFormatTemplate,
} from '../../../lib/services/format-template.service';
import {
  buildAgeGroupBucketOptions,
  buildStageBlueprintsFromStages,
} from '../../../lib/helpers/format-template.helpers';
import { uploadFileToSignedUrl } from '../../../lib/utils/upload.utils';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { KNOWN_AGE_GROUP_BUCKETS } from '../format-template.constants';
import { messages } from '../format-template.messages';
import { SaveFormatTemplateModal } from './save-format-template-modal.component';
import { ISaveFormatTemplateFields } from './save-format-template-modal.types';
import { validationSchema } from './save-format-template-modal.validation';

interface ISaveFormatTemplateModalContainerProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  selectedStages: IAssessmentStage[];
  companyId: string;
  ageGroup?: IAgeGroup | null;
}

export const SaveFormatTemplateModalContainer: React.FC<ISaveFormatTemplateModalContainerProps> = ({
  open,
  onClose,
  onSaved,
  selectedStages,
  companyId,
  ageGroup,
}) => {
  const { createFormatTemplate } = buildCreateFormatTemplate();
  const { updateFormatTemplate } = buildUpdateFormatTemplate();
  const { issueFormatTemplateDiagramUpload } = buildIssueFormatTemplateDiagramUpload();

  // The diagram file is held locally until submit — minting its upload needs the template _id,
  // which only exists after create. Submit chains create → mint → PUT → update(imagePath).
  const [pendingDiagram, setPendingDiagram] = useState<{ file: File; previewUrl: string } | null>(null);
  // Set once create succeeds, so a resubmit after a failed diagram step updates the existing
  // template instead of re-creating (the unique name index would reject the duplicate).
  const createdTemplateIdRef = useRef<string | null>(null);
  useEffect(() => {
    createdTemplateIdRef.current = null;
    setPendingDiagram(prev => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, [open]);

  const handleSelectDiagram = useCallback((file: File) => {
    setPendingDiagram(prev => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return { file, previewUrl: URL.createObjectURL(file) };
    });
  }, []);

  // Seed and offer the server's bucket for the source age group, so a bucket outside the built-in
  // list can still tag the template with the group it was built for.
  const ageGroupBucket = ageGroup?.bucket ?? null;
  const initialValues: ISaveFormatTemplateFields = useMemo(() => ({
    name: '',
    ageGroupBuckets: ageGroupBucket ? [ageGroupBucket] : [],
    minPlayers: '',
    maxPlayers: '',
    teamOutcomes: '',
  }), [ageGroupBucket]);

  const bucketOptions = useMemo(
    () => buildAgeGroupBucketOptions(KNOWN_AGE_GROUP_BUCKETS, ageGroupBucket ? [ageGroupBucket] : []),
    [ageGroupBucket],
  );

  const handleSubmit = useCallback(async (
    values: ISaveFormatTemplateFields,
    { setSubmitting, setStatus }: FormikHelpers<ISaveFormatTemplateFields>,
  ) => {
    setStatus(undefined);

    const metadata = {
      name: values.name.trim(),
      ageGroupBuckets: values.ageGroupBuckets.length ? values.ageGroupBuckets : undefined,
      minPlayers: values.minPlayers === '' ? undefined : Number(values.minPlayers),
      maxPlayers: values.maxPlayers === '' ? undefined : Number(values.maxPlayers),
      teamOutcomes: values.teamOutcomes.split(',').map(t => t.trim()).filter(Boolean),
    };

    let templateId = createdTemplateIdRef.current;
    try {
      // Inside the try so a stale-stage throw is caught, surfaces a friendly status, and still
      // reaches finally { setSubmitting(false) } instead of leaving the button stuck spinning.
      if (!templateId) {
        const blueprints = buildStageBlueprintsFromStages(selectedStages);
        const result = await createFormatTemplate({ ...metadata, companyId, stageBlueprints: blueprints });
        templateId = result.data?.createFormatTemplate?._id ?? null;
        createdTemplateIdRef.current = templateId;
      } else {
        // Retry after a failed diagram step: re-apply the metadata too, so an edit made
        // between attempts isn't silently dropped by skipping the create.
        await updateFormatTemplate(templateId, metadata);
      }
    } catch (err) {
      console.error('createFormatTemplate failed:', err);
      setStatus(<IntlMsg {...messages.saveError} />);
      setSubmitting(false);
      return;
    }

    try {
      if (templateId && pendingDiagram) {
        const issued = await issueFormatTemplateDiagramUpload(templateId, pendingDiagram.file.type);
        if (!issued) throw new Error('issueFormatTemplateDiagramUpload returned no capability');
        // PUT with the contentType the signed URL binds (see useFormatTemplateDiagramUpload).
        await uploadFileToSignedUrl(issued.uploadUrl, pendingDiagram.file, issued.contentType);
        await updateFormatTemplate(templateId, { imagePath: issued.imagePath });
      }
      onSaved();
    } catch (err) {
      console.error('format template diagram upload failed:', err);
      setStatus(<IntlMsg {...messages.saveDiagramError} />);
    } finally {
      setSubmitting(false);
    }
  }, [selectedStages, companyId, createFormatTemplate, updateFormatTemplate,
    issueFormatTemplateDiagramUpload, pendingDiagram, onSaved]);

  return (
    // key resets the form each time the modal reopens — the Formik provider stays mounted.
    <Formik<ISaveFormatTemplateFields>
      key={open ? 'open' : 'closed'}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => (
        <SaveFormatTemplateModal
          open={open}
          onClose={onClose}
          stageCount={selectedStages.length}
          bucketOptions={bucketOptions}
          form={form}
          pendingDiagramUrl={pendingDiagram?.previewUrl}
          onSelectDiagram={handleSelectDiagram}
        />
      )}
    </Formik>
  );
};
